import { cn, tv } from "tailwind-variants";
import type { ContentFeedItemDto } from "#/_generated/umbraco/delivery-api/models";
import HtmlMarkup from "#/_packages/umbrella/components/ui/html-markup.tsx";
import { Heading } from "#/_packages/umbrella/components/ui/primitives/heading.tsx";
import { useAnimationEffect } from "#/_packages/umbrella/integrations/umbraco/hooks/use-animation-effect.ts";
import type {
	AnimationEffectType,
	AnimationScrollType,
	HeadingLevel,
	Intent,
	Variant,
} from "#/_packages/umbrella/types";
import { formatPublishDate } from "#/_packages/umbrella/utils/date-utils.ts";
import { getAltTextFromString } from "#/_packages/umbrella/utils/image-utils.ts";
import { m } from "#/paraglide/messages";

const contentFeedItem = tv({
	slots: {
		base: "block__teaser flex flex-col relative overflow-hidden rounded-(--umb-border-radius-lg) h-full",
	},
	variants: {
		variant: {
			solid: { base: "teaser-variant-solid" },
			outline: { base: "teaser-variant-outline" },
			ghost: { base: "teaser-variant-ghost" },
			soft: { base: "teaser-variant-soft" },
			quiet: { base: "teaser-variant-quiet" },
		},
		intent: {
			primary: { base: "teaser-intent-primary" },
			secondary: { base: "teaser-intent-secondary" },
			light: { base: "teaser-intent-light" },
			dark: { base: "teaser-intent-dark" },
			white: { base: "teaser-intent-white" },
			black: { base: "teaser-intent-black" },
			success: { base: "teaser-intent-success" },
			warning: { base: "teaser-intent-warning" },
			danger: { base: "teaser-intent-danger" },
			info: { base: "teaser-intent-info" },
			none: { base: "teaser-intent-none" },
		},
		orientation: {
			vertical: {
				base: "teaser-orientation-vertical",
			},
			horizontal: {
				base: "teaser-orientation-horizontal",
			},
		},
		animationScrollType: {
			none: { base: "" },
			"anim-view-fadeIn-moveUp": { base: "anim-view-fadeIn-moveUp" },
			"anim-view-fadeIn-moveDown": { base: "anim-view-fadeIn-moveDown" },
			"anim-view-fadeIn-moveLeft": { base: "anim-view-fadeIn-moveLeft" },
		},
		alignmentSelfVertical: {
			stretch: { base: "" },
			start: { base: "self-start" },
			center: { base: "self-center" },
			end: { base: "self-end" },
		},
		imagePosition: {
			start: { base: "" },
			end: { base: "teaser-img-end" },
		},
	},
	defaultVariants: {
		intent: "none",
		variant: "solid",
	},
});

type ContentFeedItemProps = {
	item: ContentFeedItemDto;
	hideImage?: boolean;
	variant?: Variant;
	intent?: Intent;
	orientation?: string;
	imagePositionEnd?: boolean;
	hideHeading?: boolean;
	hideIntro?: boolean;
	hidePublishDate?: boolean;
	headingLevel: HeadingLevel;
	animationScrollType?: AnimationScrollType | "";
	animationEffectType?: AnimationEffectType | string;
	hoverItemClasses?: string | null;
	hoverMediaClasses?: string | null;
};

export default function ContentFeedItem({
	item,
	variant,
	intent,
	orientation,
	hideImage,
	imagePositionEnd,
	hideIntro,
	hidePublishDate,
	hideHeading,
	headingLevel,
	animationScrollType,
	animationEffectType = "none",
	hoverItemClasses,
	hoverMediaClasses,
}: ContentFeedItemProps) {
	const isHorizontal =
		orientation === "orientation-horizontal" || orientation === "horizontal";
	const introText = item.intro ?? item.metaDescription;
	const publishDate = formatPublishDate(item.publishDate);

	const { base } = contentFeedItem({
		intent,
		variant,
		orientation: isHorizontal ? "horizontal" : "vertical",
		imagePosition: imagePositionEnd ? "end" : "start",
	});
	const animationEffect = useAnimationEffect({
		animationEffectType,
		intentColor: intent ?? "none",
	});

	return (
		<div
			key={item.id}
			style={animationEffect.style}
			data-anim-effect={animationEffect["data-anim-effect"]}
			onPointerEnter={animationEffect.onPointerEnter}
			onPointerLeave={animationEffect.onPointerLeave}
			onPointerMove={animationEffect.onPointerMove}
			className={cn(
				base(),
				hoverItemClasses,
				animationScrollType,
				animationEffect.className,
			)}
		>
			{!hideImage && item.image ? (
				<figure className={cn("figure", hoverMediaClasses)}>
					<img
						src={item.image.src ?? undefined}
						alt={getAltTextFromString(item.image.alt ?? undefined)}
						loading="lazy"
						className="w-full object-cover aspect-video"
					/>
				</figure>
			) : null}
			<div
				className={cn(
					"teaser-content flex flex-col gap-4 p-teaser flex-1 mb-auto",
				)}
			>
				{!hideHeading ? (
					<header>
						<Heading level={headingLevel} className="teaser-title">
							{item.title}
						</Heading>
					</header>
				) : null}
				{!hideIntro && introText ? (
					<div className="teaser-body">
						<HtmlMarkup
							markup={introText}
							className="text-truncate-multi-2 mb-0-last line-clamp-3 leading-6"
						/>
					</div>
				) : null}
				{!hidePublishDate && publishDate ? (
					<div className="meta mt-auto">
						<span className="text-label">{m.published()}</span> {publishDate}
					</div>
				) : null}
			</div>
			<a
				href={item.href}
				className="stretched-link absolute inset-0 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-inset"
				aria-label={item.title}
			>
				<span className="sr-only">{item.title}</span>
			</a>
		</div>
	);
}
